import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { translate } from 'translations/i18n';
import { withApollo } from '@apollo/client/react/hoc';
import CCEvents, {
  EventNames,
  EventPropertyKeys,
  EventPropertyValues,
} from '@companycam/companycam-events';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { isNullOrEmpty } from 'helpers/utils';
import errorLogger from 'helpers/error-logger';
import { fetchUsers } from 'actions/users';
import { fetchGroups } from 'actions/groups';
import {
  mentionTokensSelector,
  mentionGroupTokensSelector,
} from 'selectors/mentionTokens';
import { timeNowServerFormat } from 'helpers/date';
import { Steven } from 'helpers/steven';
import PusherService from 'components/core/PusherService';
import CommentList from 'components/activity/CommentList';
import TokenizedInput from 'components/activity/TokenizedInput';
import {
  ActivitiesDataProvider,
  updateCommentsCacheFromPusherEvent,
  updateVoiceNotesCacheFromPusherEvent,
} from 'components/activity/graphql/ActivitiesDataProvider';
import MutationAddComment from 'components/activity/graphql/MutationAddComment';
import RecordableTypes from 'constants/recordableTypes';
import { AssetModalSidebarHeader } from '../assets/shared/styles';
import { LD_DYNAMIC_TRANSLATION } from 'constants/featureFlags';
import CommentListWithTranslations from 'components/activity/CommentListWithTranslations';

const eventPropertyValueMap = {
  [RecordableTypes.PHOTO]: EventPropertyValues.PHOTO,
  [RecordableTypes.VIDEO]: EventPropertyValues.VIDEO,
  [RecordableTypes.PROJECT]: EventPropertyValues.PROJECT,
};

const CommentContainer = ({ client, nodeId, recordableType, projectId }) => {
  const dispatch = useDispatch();
  const [isPosting, setIsPosting] = useState(false);
  useEffect(() => {
    dispatch(fetchUsers());
    dispatch(fetchGroups());
  }, [dispatch]);
  const groupTokens = useSelector((state) => mentionGroupTokensSelector(state));
  const userTokens = useSelector((state) => mentionTokensSelector(state));
  const tokens = [...groupTokens, ...userTokens];

  const flags = useFlags();
  const translationServices = [
    'gpt-4o-mini',
    'amazon.nova-lite-v1:0',
    'anthropic.claude-3-sonnet-20240229-v1:0',
  ];
  const isDynamicTranslationEnabled = translationServices.includes(
    flags[LD_DYNAMIC_TRANSLATION],
  );

  const receivePusherEvent = (event) => {
    switch (event.type) {
      case 'comment':
        updateCommentsCacheFromPusherEvent({
          cache: client,
          event,
        });
        break;
      case 'voice_note':
        updateVoiceNotesCacheFromPusherEvent({
          cache: client,
          event,
        });
        break;
      default:
        break;
    }
  };

  const submitComment = ({ text, metadata, addComment }) => {
    // CommentCreateInput GraphQL object
    const commentCreateInput = {
      recordableId: nodeId,
      createdAt: timeNowServerFormat(),
      comment: text,
    };
    if (Array.isArray(metadata) && metadata.length > 0) {
      commentCreateInput.metadata = JSON.stringify({ users: metadata });
    }

    const didMention = !isNullOrEmpty(commentCreateInput.metadata);

    // Set isPosting true until done
    setIsPosting(true);
    addComment(commentCreateInput)
      .then((data) => {
        // If there are errors, don't log the event
        if (data.data.commentCreate.errors.length > 0) {
          return;
        }

        // Log successful comment
        CCEvents.logEvent(EventNames.COMMENT, {
          [EventPropertyKeys.COMMENTABLE]:
            eventPropertyValueMap[recordableType],
          [EventPropertyKeys.DID_MENTION]: didMention,
        });

        Steven.entityCrud({
          entity_id: data.data.commentCreate.newCommentEdge.node.databaseId,
          entity_type: 'comment',
          action: 'created',
          source: recordableType,
        });

        if (didMention) {
          // Log successful mention
          metadata.forEach((mentionedUser) => {
            Steven.entityCrud({
              entity_id: mentionedUser.value.toString(),
              entity_type: 'mention',
              action: 'created',
              source: 'comment',
            });
          });
        }
      })
      .catch((error) => {
        errorLogger({ error });
      })
      .then(() => {
        setIsPosting(false);
      });
  };

  const renderView = ({
    fetchingResults,
    activities,
    loadMore,
    canLoadMore,
    addComment,
  }) => {
    const addCommentPlaceholder = translate(
      'activity.commentContainer.addComment',
    );

    return (
      <>
        <AssetModalSidebarHeader
          className="scroll-container-header"
          data-testid={
            recordableType === RecordableTypes.PROJECT
              ? 'project__comments-header'
              : 'asset__comments__header'
          }
        >
          {translate('activity.commentList.photoActivity')}
        </AssetModalSidebarHeader>
        <PusherService onActivityReceived={receivePusherEvent} />
        {isDynamicTranslationEnabled ? (
          <CommentListWithTranslations
            fetchingResults={fetchingResults}
            activityItems={activities}
            canLoadMore={canLoadMore}
            loadMore={loadMore}
            nodeId={nodeId}
            recordableType={recordableType}
          />
        ) : (
          <CommentList
            fetchingResults={fetchingResults}
            activityItems={activities}
            canLoadMore={canLoadMore}
            loadMore={loadMore}
          />
        )}
        <TokenizedInput
          placeholder={addCommentPlaceholder}
          submitDisabled={isPosting}
          tokenDefs={tokens}
          projectId={projectId}
          onSubmit={(text, metadata) => {
            submitComment({ text, metadata, addComment });
            return false;
          }}
          recordableType={recordableType}
        />
      </>
    );
  };

  return (
    <ActivitiesDataProvider variables={{ nodeId }}>
      {(dataProviderProps) => (
        <MutationAddComment>
          {(addCommentProps) =>
            renderView({
              ...dataProviderProps,
              ...addCommentProps,
            })
          }
        </MutationAddComment>
      )}
    </ActivitiesDataProvider>
  );
};

CommentContainer.propTypes = {
  nodeId: PropTypes.string,
  projectId: PropTypes.string,
  client: PropTypes.any,
  recordableType: PropTypes.oneOf([
    RecordableTypes.PHOTO,
    RecordableTypes.PROJECT,
    RecordableTypes.VIDEO,
  ]).isRequired,
};

const CommentContainerWithApollo = withApollo(CommentContainer);
export default CommentContainerWithApollo;
